import { useCallback, useEffect, useRef, useState } from "react";
import type { WsMessage, SlideState, PresencePayload } from "@wedding/shared";

/**
 * Admin "Sahne'yi İzle" live feed — CF-Access-authenticated WebSocket to the
 * SlideshowRoom DO via the admin-mounted reji route. WATCH-ONLY: the admin
 * never controls the show. `visible=false` connects with `?mode=observe`
 * (silent: not in presence, not counted); `visible=true` connects normally so
 * the admin appears in presence as role "admin" (id "admin:<email>") + counts.
 * Toggling `visible` reconnects with the other mode.
 *
 * Ported from apps/organizer use-reji-ws: 45s ping keepalive (DO auto-pongs
 * without waking from hibernation), backoff+jitter reconnect, full cleanup.
 */
export function useAdminSahneWs(slug: string, visible: boolean) {
  const observe = !visible;
  const [slideState, setSlideState] = useState<SlideState | null>(null);
  const [presence, setPresence] = useState<PresencePayload | null>(null);
  const [connected, setConnected] = useState(false);
  const clockOffsetRef = useRef(0);

  const wsRef = useRef<WebSocket | null>(null);
  const cancelledRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptRef = useRef(0);

  const connect = useCallback(() => {
    if (cancelledRef.current) return;
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(
      `${protocol}//${window.location.host}/api/admin/w/${slug}/slideshow/ws${observe ? "?mode=observe" : ""}`,
    );
    ws.onopen = () => {
      setConnected(true);
      attemptRef.current = 0;
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send("ping");
      }, 45000);
    };
    ws.onclose = () => {
      setConnected(false);
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
      // Only reconnect if THIS socket is still the active one. Toggling
      // visibility intentionally closes the old socket + opens a new one; without
      // this guard the old socket's onclose would re-open with the OLD mode —
      // e.g. going hidden would silently re-open a VISIBLE socket ("can't hide").
      if (!cancelledRef.current && wsRef.current === ws) {
        const delay = Math.min(60000, 3000 * 2 ** attemptRef.current) * (0.8 + Math.random() * 0.4);
        attemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      }
    };
    ws.onmessage = (event) => {
      if (event.data === "pong") return;
      let msg: WsMessage;
      try {
        msg = JSON.parse(event.data as string) as WsMessage;
      } catch {
        return;
      }
      if (msg.type === "slide_state" && msg.slideState) {
        clockOffsetRef.current = msg.slideState.serverNow - Date.now();
        setSlideState(msg.slideState);
      } else if (msg.type === "presence" && msg.presence) {
        setPresence(msg.presence);
      }
    };
    wsRef.current = ws;
  }, [slug, observe]);

  useEffect(() => {
    cancelledRef.current = false;
    connect();
    return () => {
      cancelledRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return { slideState, presence, connected, clockOffsetRef };
}
